import { Link } from 'react-router-dom';
import { Shield, Activity, Zap, Settings as SettingsIcon } from 'lucide-react';

export default function BlogPolymarketRiskManagement() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        {/* Header */}
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600/10 border border-emerald-500/30 text-xs text-emerald-200 uppercase tracking-wide">
            <Shield className="w-3 h-3" />
            <span>Risk Management on Polymarket</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Stop Copy Trading on Vibes: Real Risk Management for Polymarket With BetMimic
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Copy trading without risk management is just a slower way to donate. BetMimic wraps Polymarket
            copy trading in hard limits, automated monitors and live exposure checks so you can scale size
            without blowing up on one trader&apos;s heater or one crazy news event.
          </p>
        </header>

        {/* Section: The problem with naive copy trading */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">The problem with naive Polymarket copy bots</h2>
          <p className="text-sm text-dark-300">
            Most &quot;copy bots&quot; in prediction markets are just webhooks with a send button. They see a
            signal, fire an order and hope for the best. There&apos;s no concept of bankroll, exposure, daily
            loss or reconciliation against reality. That might be fine for lunch money. It&apos;s a disaster
            once you size real tickets.
          </p>
          <p className="text-sm text-dark-300">
            Polymarket is especially unforgiving because real-world news, halts and resolution edge cases
            can move prices violently. A serious Polymarket copy stack has to know:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>How much total exposure you have across all open markets.</li>
            <li>How much of that risk belongs to a single trader, config or narrative.</li>
            <li>When to stop trading for the day because your PnL hit a line you promised not to cross.</li>
          </ul>
        </section>

        {/* Section: Bankroll and sizing basics */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Bankroll basics for Polymarket copy trading</h2>
          <p className="text-sm text-dark-300">
            Before you touch copy configs, decide what your Polymarket bankroll is. That&apos;s not your net
            worth, it&apos;s the amount you&apos;re genuinely okay cycling through volatile events over months.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Keep Polymarket size small relative to your total stack – think &quot;sleeve&quot;, not &quot;entire
              portfolio&quot;.
            </li>
            <li>
              Within that sleeve, cap how much a single trader or strategy can ever control.
            </li>
            <li>
              Remember that some Polymarket markets can tie up capital for months; liquidity dries up
              during long campaigns.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            A simple rule of thumb: don&apos;t let any single copy config risk more than 5–10% of your
            dedicated Polymarket bankroll at once.
          </p>
        </section>

        {/* Section: How BetMimic enforces limits */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-primary-400" />
            How BetMimic enforces risk on every copied trade
          </h2>
          <p className="text-sm text-dark-300">
            BetMimic treats every copy config like its own mini fund. Before a single order hits the
            Polymarket CLOB, the engine runs it through a full risk block:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Max exposure per config</span> – total open value across all
              markets for that trader cannot exceed the cap you set. If the next trade would push you over
              the line, it&apos;s sized down or skipped.
            </li>
            <li>
              <span className="font-medium">Max open positions</span> – you can limit how many distinct
              markets a config can touch at once, so one trader doesn&apos;t spray 40 micro‑markets at you.
            </li>
            <li>
              <span className="font-medium">Daily trade and loss limits</span> – optional caps that stop
              copying once you&apos;ve hit a certain number of trades or a daily PnL drawdown for that config.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            All of this runs in the backend engine, not your browser. Even if you close the site, your
            guardrails keep working as long as a config is active.
          </p>
        </section>

        {/* Section: Per-position SL/TP and monitoring */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-400" />
            Per-position stop loss, take profit and auto-closing
          </h2>
          <p className="text-sm text-dark-300">
            On Polymarket, the worst trades are the ones you refuse to close. BetMimic&apos;s position monitor
            continuously checks each open market against your stop loss and take profit rules.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              You can set default SL/TP percentages per copy config so every new position inherits those
              guardrails automatically.
            </li>
            <li>
              You can override SL/TP on a single position from the Positions page if a trade needs special
              treatment or looser/tighter risk.
            </li>
            <li>
              When price crosses a trigger, the engine sends a real sell order via Polymarket&apos;s CLOB
              client, updates position status and records realized PnL in the same place you see unrealized
              PnL.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            Think of it as a robot sitting in your positions tab, checking whether any line has hit the
            levels you promised yourself you&apos;d respect.
          </p>
        </section>

        {/* Section: Live balances and reconciliation */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Live balances, allowances and position reconciliation</h2>
          <p className="text-sm text-dark-300">
            Risk management means nothing if the numbers are stale. BetMimic keeps your view of exposure
            lined up with what Polymarket and Polygon actually see for your wallet.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              The engine periodically checks USDC.e balances and on-chain allowances before firing copies
              and forces an immediate check on engine startup and wallet connect/change.
            </li>
            <li>
              Open positions are reconciled against Polymarket&apos;s Data API; ghost positions get auto closed
              so you don&apos;t manage risk on markets that are already flat or redeemed.
            </li>
            <li>
              Redeemable markets are auto claimed and the resulting PnL flows into your dashboard totals,
              instead of leaving "stuck value" in dead markets.
            </li>
          </ul>
        </section>

        {/* Section: Putting it together with an example */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            Example: building a sane risk box
          </h2>
          <p className="text-sm text-dark-300">
            Imagine you want to allocate <span className="font-mono">$1,000</span> to Polymarket copy trading.
            You decide no single trader should ever control more than <span className="font-mono">$300</span>{' '}
            of that at once.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>Set <span className="font-medium">maxExposure</span> on a config to $300.</li>
            <li>Use fixed sizing of $10–$20 per trade so you get many small tickets, not one mega bet.</li>
            <li>
              Enable a daily loss limit of $50–$75 so if an event day goes badly, the config auto-pauses
              until tomorrow.
            </li>
            <li>
              Turn on SL/TP defaults at −30% / +80% so single lines don&apos;t drift against you for weeks.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            None of these numbers are magic, but having numbers at all puts you far ahead of &quot;send it and
            pray&quot; copy trading.
          </p>
        </section>

        {/* CTA / Internal links */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            If you&apos;re going to copy trade Polymarket with real size, you need more than a webhook. You
            need guardrails that live in the engine and respect what the chain and the exchange are
            actually doing.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/copy-configs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Shield className="w-4 h-4" />
              Tune your first risk-managed copy config
            </Link>
            <Link
              to="/tutorial"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              Read the full BetMimic risk &amp; PnL tutorial
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
